import React, { useCallback } from "react";
import {
  ModalBody,
  ModalFooter,
} from "../../../design-system/components/composite/Modal/Modal";
import { Button } from "../../atoms";
import {
  DateField,
  Dropdown,
  MonthlyDayField,
  RefreshAtField,
  RepeatEveryField,
  ScheduleEndsField,
  WeekdayPillRow,
} from "../../molecules";
import {
  Field,
  FieldLabel,
  HelperText,
} from "../../molecules/shared/scheduleField.styles";
import { usePortalMenu } from "../../../hooks/usePortalMenu";
import { spacing } from "../../../design-system/tokens/spacing";
import {
  REPEAT_OPTIONS,
  UNIT_WORD,
} from "../../../utils/storyRefreshConstants";
import type { RepeatPreset } from "../../../utils/storyRefreshTypes";
import { useStoryScheduleRefresh } from "./useStoryScheduleRefresh";
import {
  FooterAction,
  FooterRow,
  Form,
  Header,
  HeaderTitle,
  StoryModal,
} from "./StoryScheduleRefreshModal.styles";
import type { StoryScheduleRefreshModalProps } from "./StoryScheduleRefreshModal.types";

/** Figma pads the header 16 and the body/footer 12. */
const HEADER_PADDING = spacing[4];
const BODY_PADDING = spacing[3];

/**
 * "Schedule refresh" for a data story — Figma nodes 5401:217407 (does not
 * repeat), 5401:217951 (daily), 5401:217658 (weekly), 5407:218108 (monthly).
 *
 * One `Repeat` dropdown drives the whole form: the sections below it swap per
 * preset, so this component is mostly a switch. Each section is its own
 * molecule, and every control is an existing primitive (`Dropdown`,
 * `DateField`, `DayPill`, `NumberStepper`) — nothing about a date picker or a
 * menu is reimplemented here.
 *
 * State, validation and which-dropdown-is-open live in
 * `useStoryScheduleRefresh`; this file is markup and wiring.
 */
export const StoryScheduleRefreshModal: React.FC<
  StoryScheduleRefreshModalProps
> = ({ open, onClose, storyId, initialValue, onSubmit, isSaving = false, anchor }) => {
  const {
    config,
    errors,
    showErrors,
    openDropdown,
    timezoneQuery,
    patch,
    setRepeatPreset,
    toggleWeekday,
    openDropdownKey,
    setTimezoneQuery,
    closeDropdown,
    attemptSubmit,
  } = useStoryScheduleRefresh({ initialValue, open });

  const repeatPortal = usePortalMenu<HTMLButtonElement, HTMLUListElement>({
    open: openDropdown === "repeat",
    onOutsideClick: closeDropdown,
  });

  const handleSave = useCallback(() => {
    if (isSaving) return;
    if (!attemptSubmit()) return;
    void onSubmit(storyId, config);
  }, [attemptSubmit, config, isSaving, onSubmit, storyId]);

  const hourOrTimezone =
    openDropdown === "hour" || openDropdown === "timezone"
      ? openDropdown
      : null;

  const scheduleAt = (
    <RefreshAtField
      hour={config.refreshHour}
      timezone={config.timezone}
      label="Schedule at"
      showTimezone
      openDropdown={hourOrTimezone}
      timezoneQuery={timezoneQuery}
      onChangeHour={(h) => patch({ refreshHour: h })}
      onChangeTimezone={(tz) => patch({ timezone: tz })}
      onTimezoneQueryChange={setTimezoneQuery}
      onOpenDropdown={openDropdownKey}
      onCloseDropdown={closeDropdown}
      hourError={errors.refreshHour}
      timezoneError={errors.timezone}
      showError={showErrors}
    />
  );

  const renderEnds = (showAfter: boolean) => (
    <ScheduleEndsField
      mode={config.endsMode}
      endsOn={config.endsOn}
      endsAfter={config.endsAfter}
      minDate={config.startsOn || undefined}
      onChangeMode={(endsMode) => patch({ endsMode })}
      onChangeEndsOn={(endsOn) => patch({ endsOn })}
      onChangeEndsAfter={(endsAfter) => patch({ endsAfter })}
      endsOnError={errors.endsOn}
      endsAfterError={errors.endsAfter}
      showError={showErrors}
      showAfter={showAfter}
    />
  );

  const startsOn = (
    <Field>
      <FieldLabel>Starts on</FieldLabel>
      <DateField
        value={config.startsOn}
        placeholder="Pick a start date"
        invalid={showErrors && Boolean(errors.startsOn)}
        ariaLabel="Starts on"
        iconLayout="calendar-end"
        onChange={(startsOn) => patch({ startsOn })}
      />
      {showErrors && errors.startsOn && (
        <HelperText $error>{errors.startsOn}</HelperText>
      )}
    </Field>
  );

  const renderPresetFields = () => {
    switch (config.repeat) {
      // One-off: the date spans the modal, then time and timezone share a row.
      case "none":
        return (
          <>
            <Field>
              <FieldLabel>Schedule on</FieldLabel>
              <DateField
                value={config.scheduleOn}
                placeholder="Pick a date"
                invalid={showErrors && Boolean(errors.scheduleOn)}
                ariaLabel="Schedule on"
                iconLayout="calendar-end"
                onChange={(scheduleOn) => patch({ scheduleOn })}
              />
              {showErrors && errors.scheduleOn && (
                <HelperText $error>{errors.scheduleOn}</HelperText>
              )}
            </Field>
            {scheduleAt}
          </>
        );

      case "daily":
        return scheduleAt;

      case "weekly":
        return (
          <>
            <RepeatEveryField
              value={config.every}
              unitWord={UNIT_WORD.weekly ?? "Week"}
              onChange={(every) => patch({ every })}
              error={errors.every}
              showError={showErrors}
            />
            <WeekdayPillRow
              value={config.repeatOn}
              onToggle={toggleWeekday}
              error={errors.repeatOn}
              showError={showErrors}
            />
            {scheduleAt}
            {startsOn}
            {renderEnds(true)}
          </>
        );

      case "monthly":
        return (
          <>
            <RepeatEveryField
              value={config.every}
              unitWord={UNIT_WORD.monthly ?? "Month"}
              onChange={(every) => patch({ every })}
              error={errors.every}
              showError={showErrors}
            />
            {scheduleAt}
            {startsOn}
            <MonthlyDayField
              value={config.monthlyRepeatOn}
              startsOn={config.startsOn}
              open={openDropdown === "repeatDom"}
              onSelect={(monthlyRepeatOn) => patch({ monthlyRepeatOn })}
              onToggle={() => openDropdownKey("repeatDom")}
              onClose={closeDropdown}
              error={errors.monthlyRepeatOn}
              showError={showErrors}
            />
            {renderEnds(true)}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <StoryModal
      open={open}
      onClose={onClose}
      size="sm"
      closeOnBackdropClick={false}
      anchor={anchor}
    >
      <Header showCloseButton onClose={onClose} padding={HEADER_PADDING}>
        <HeaderTitle>Schedule refresh</HeaderTitle>
      </Header>

      <ModalBody padding={BODY_PADDING}>
        <Form>
          <Field>
            <FieldLabel>Repeat</FieldLabel>
            <Dropdown<RepeatPreset>
              value={config.repeat}
              options={REPEAT_OPTIONS}
              open={openDropdown === "repeat"}
              triggerRef={repeatPortal.triggerRef}
              menuRef={repeatPortal.menuRef}
              menuRect={repeatPortal.rect}
              size="md"
              compact
              fullWidth
              ariaLabel="Repeat"
              onToggle={() => openDropdownKey("repeat")}
              onSelect={(next) => {
                setRepeatPreset(next);
                closeDropdown();
              }}
            />
          </Field>

          {renderPresetFields()}
        </Form>
      </ModalBody>

      <ModalFooter padding={BODY_PADDING}>
        <FooterRow>
          <FooterAction>
            <Button
              variant="secondary-outline"
              size="md"
              fullWidth
              disabled={isSaving}
              onClick={onClose}
            >
              Cancel
            </Button>
          </FooterAction>
          <FooterAction>
            <Button
              variant="primary-dark"
              size="md"
              fullWidth
              loading={isSaving}
              disabled={isSaving}
              onClick={handleSave}
            >
              Save schedule
            </Button>
          </FooterAction>
        </FooterRow>
      </ModalFooter>
    </StoryModal>
  );
};

export default StoryScheduleRefreshModal;
